import React, { useState, useEffect, useRef } from 'react';

const ClientsSection = ({ colors, visibleSections }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0 });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        console.log('Fetching clients...');
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Clients_ClientList');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Clients API response:', data);
        
        let clientsData = [];
        
        if (Array.isArray(data)) {
          clientsData = data;
        } else if (data && Array.isArray(data.data)) {
          clientsData = data.data;
        } else if (data && typeof data.data === 'object' && data.data !== null) {
          clientsData = Object.values(data.data);
        } else if (data && typeof data === 'object') {
          const possibleArrays = Object.values(data).filter(val => Array.isArray(val));
          if (possibleArrays.length > 0) {
            clientsData = possibleArrays[0];
          }
        }
        
        console.log('Processed clients data:', clientsData);
        setClients(clientsData);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setIsPaused(true);
    dragStartRef.current = {
      x: e.pageX,
      scrollLeft: scrollContainerRef.current.scrollLeft
    };
    scrollContainerRef.current.style.scrollBehavior = 'auto';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX;
    const walk = (dragStartRef.current.x - x) * 2;
    scrollContainerRef.current.scrollLeft = dragStartRef.current.scrollLeft + walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.scrollBehavior = 'smooth';
    }
    setTimeout(() => setIsPaused(false), 2000);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = 'smooth';
      }
      setTimeout(() => setIsPaused(false), 2000);
    }
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    if (!scrollContainerRef.current) return;
    setIsPaused(true);
    dragStartRef.current = {
      x: e.touches[0].pageX,
      scrollLeft: scrollContainerRef.current.scrollLeft
    };
  };

  const handleTouchEnd = () => {
    setTimeout(() => setIsPaused(false), 2000);
  };

  if (loading) {
    return (
      <section className="py-5" style={{ backgroundColor: colors.white, minHeight: '200px' }}>
        <div className="container">
          <div className="text-center">
            <div className="spinner-border" style={{ color: colors.primary }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2" style={{ color: colors.gray }}>Loading clients...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5" style={{ backgroundColor: colors.white, minHeight: '200px' }}>
        <div className="container">
          <div className="text-center">
            <p style={{ color: 'red' }}>Error loading clients: {error}</p>
            <button 
              className="btn btn-primary mt-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <section className="py-5" style={{ backgroundColor: colors.white, minHeight: '200px' }}>
        <div className="container">
          <div className="text-center">
            <h3>No clients data available</h3>
            <p style={{ color: colors.gray }}>The API returned no client information.</p>
            <button 
              className="btn btn-primary mt-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="py-4" 
      style={{ backgroundColor: colors.white }}
      data-section="clients"
    >
      <div className="container">
        {/* Desktop View - Grid Layout */}
        <div className="d-none d-md-block">
          <div className="row g-3 justify-content-center">
            {clients.map((client, index) => (
              <div key={client.id || index} className="col-6 col-sm-4 col-md-3 col-lg-2">
                <div className="text-center p-3">
                  <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{ 
                      height: '70px',
                      padding: '1rem'
                    }}
                  >
                    {client.companyLogo ? (
                      <img 
                        src={`https://api.dremerz.net/uploads/${client.companyLogo}`}
                        alt={client.companyname || 'Client Logo'}
                        className="img-fluid client-logo"
                        style={{ 
                          maxHeight: '80px',
                          maxWidth: '100%',
                          objectFit: 'contain',
                          transition: 'transform 0.3s ease',
                          // cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        onError={(e) => {
                          console.error('Image failed to load:', client.companyLogo);
                          e.target.src = 'https://via.placeholder.com/100x50?text=Logo';
                        }}
                      />
                    ) : (
                      <div 
                        className="d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '80px',
                          height: '50px',
                          backgroundColor: '#f0f0f0',
                          color: '#999',
                          fontSize: '0.7rem'
                        }}
                      >
                        <span>No Logo</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile View - Auto Horizontal Scrolling with Touch */}
        <div className="d-block d-md-none">
          <div className="mobile-clients-scroll-container">
            <div 
              ref={scrollContainerRef}
              className="mobile-clients-scroll"
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div className={`scrolling-content-wrapper ${isPaused ? 'paused' : ''}`}>
                {/* Triple the content for seamless infinite scroll */}
                {[...Array(3)].map((_, setIndex) => (
                  <div key={`set-${setIndex}`} className="scrolling-content">
                    {clients.map((client, index) => (
                      <div key={`${setIndex}-${client.id || index}`} className="client-item-mobile">
                        <div className="text-center px-2">
                          <div 
                            className="d-flex align-items-center justify-content-center mx-auto logo-container-mobile"
                            style={{ 
                              height: '80px', // Increased height to prevent overflow
                              width: '110px', // Increased width for better spacing
                              padding: '0.5rem' // Increased padding
                            }}
                          >
                            {client.companyLogo ? (
                              <img 
                                src={`https://api.dremerz.net/uploads/${client.companyLogo}`}
                                alt={client.companyname || 'Client Logo'}
                                className="img-fluid client-logo-mobile"
                                style={{ 
                                  maxHeight: '70px', // Adjusted max height
                                  maxWidth: '100%',
                                  objectFit: 'contain',
                                  pointerEvents: 'none',
                                  display: 'block'
                                }}
                                draggable="false"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/80x40?text=Logo';
                                }}
                              />
                            ) : (
                              <div 
                                className="d-flex align-items-center justify-content-center"
                                style={{ 
                                  width: '80px',
                                  height: '40px',
                                  backgroundColor: '#f0f0f0',
                                  color: '#999',
                                  borderRadius: '4px',
                                  fontSize: '0.6rem'
                                }}
                              >
                                <span>No Logo</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .mobile-clients-scroll-container {
            overflow: visible !important; /* Changed from hidden to visible */
            position: relative;
            width: 100%;
            padding: 1rem 0; /* Increased padding top and bottom */
            margin: 0.5rem 0; /* Added margin for extra space */
          }
          
          .mobile-clients-scroll {
            overflow-x: auto;
            overflow-y: visible !important; /* Ensure vertical overflow is visible */
            width: 100%;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
            scroll-behavior: smooth;
            padding: 10px 0; /* Added padding to prevent clipping */
          }
          
          .mobile-clients-scroll::-webkit-scrollbar {
            display: none;
          }
          
          .scrolling-content-wrapper {
            display: flex;
            width: max-content;
            animation: scroll-left 45s linear infinite; /* Increased from 30s to 45s for slower scroll */
            will-change: transform;
          }
          
          .scrolling-content-wrapper.paused {
            animation-play-state: paused;
          }
          
          @keyframes scroll-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-33.333%);
            }
          }
          
          .scrolling-content {
            display: flex;
            flex-shrink: 0;
            align-items: center; /* Center items vertically */
          }
          
          .client-item-mobile {
            flex-shrink: 0;
            margin: 0 10px; /* Increased margin for better spacing */
            display: flex;
            align-items: center;
          }
          
          .logo-container-mobile {
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: visible !important; /* Ensure logos are not clipped */
          }
          
          .client-logo-mobile {
            max-height: 70px !important;
            width: auto;
            object-fit: contain;
          }
          
          /* Prevent text selection while dragging */
          .mobile-clients-scroll.grabbing * {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          
          /* Responsive adjustments */
          @media (max-width: 576px) {
            .mobile-clients-scroll-container {
              padding: 0.8rem 0; /* Increased padding */
            }
            
            .client-item-mobile {
              margin: 0 8px;
            }
            
            .scrolling-content-wrapper {
              animation: scroll-left 40s linear infinite; /* Slower scroll for small screens */
            }
            
            .logo-container-mobile {
              height: 75px !important; /* Adjusted height */
              width: 100px !important; /* Adjusted width */
            }
          }
          
          @media (max-width: 375px) {
            .mobile-clients-scroll-container {
              padding: 0.6rem 0; /* Increased padding */
            }
            
            .scrolling-content-wrapper {
              animation: scroll-left 35s linear infinite; /* Even slower for very small screens */
            }
            
            .logo-container-mobile {
              height: 70px !important;
              width: 90px !important;
            }
          }
          
          /* Reduced motion accessibility */
          @media (prefers-reduced-motion: reduce) {
            .scrolling-content-wrapper {
              animation: none;
            }
          }
          
          /* Ensure logos are fully visible */
          .mobile-clients-scroll * {
            overflow: visible !important;
          }
        `}</style>
      </div>
    </section>
  );
};

export default ClientsSection;